import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@shared/lib/http/axiosBaseQuery";
import { stableSerialize } from "@shared/lib/http/stableSerialize";
import { Car, CarAdd, CarEdit, CarSchema, CarsPaginatedSchema, CarsResponse } from "@models/car";
import {
  CAR_BROWSE_INCLUDES,
  CAR_BROWSE_PER_PAGE,
  CarSearchParams,
  getCarSearchBody,
} from "./carsBrowse.utils";
import { seedDetailCacheFromBrowse } from "@features/tickets/shared/utils/seedDetailCacheFromBrowse";


export const carsApi = createApi({
  reducerPath: "carsApi",
  baseQuery: axiosBaseQuery({ baseUrl: "/api/" }),
  tagTypes: ["cars", "carsBrowse", "car"],
  endpoints: (b) => ({

    searchCarsBrowse: b.query<CarsResponse, CarSearchParams>({
      query: searchCarsBrowseQuery,
      serializeQueryArgs: searchCarsBrowseSerializeQueryArgs,
      transformResponse: parseCarsBrowseResponse,
      providesTags: [{ type: "carsBrowse", id: "LIST" }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        seedDetailCacheFromBrowse(carsApi, "getCarById", data.data, dispatch);
      },
    }),

    getCarById: b.query<Car, string | number>({
      query: getCarByIdQuery,
      transformResponse: parseCarResponse,
      providesTags: getCarByIdProvidesTags,
    }),

    addCar: b.mutation<Car, CarAdd>({
      query: addCarQuery,
      transformResponse: parseCarResponse,
      invalidatesTags: addCarInvalidatesTags,
    }),

    saveCar: b.mutation<Car, { carId: number; carToSave: CarEdit }>({
      query: saveCarQuery,
      transformResponse: parseCarResponse,
      invalidatesTags: saveCarInvalidatesTags,
    }),

    deleteCar: b.mutation<void, number>({
      query: deleteCarQuery,
      invalidatesTags: deleteCarInvalidatesTags,
    }),

    getNextCarNumber: b.query<number, void>({
      query: getNextCarNumberQuery,
      transformResponse: getNextCarNumberTransformResponse,
    }),
  }),
});

export const {
  useSearchCarsBrowseQuery,
  useLazySearchCarsBrowseQuery,
  useGetCarByIdQuery,
  useAddCarMutation,
  useSaveCarMutation,
  useDeleteCarMutation,
  useLazyGetNextCarNumberQuery,
} = carsApi;






//         *******************************************
//         *             helper functions            *
//         *******************************************

/**
 * RTK Query cache tags to invalidate after car create, update, or delete.
 * @param carId number | undefined - Specific car id when invalidating a single record.
 * @returns Array - Cache tag descriptors for cars list, browse list, and optional detail.
 */
function carMutationTags(carId?: number) {

  return [
    { type: "cars" as const, id: "LIST" as const },
    { type: "carsBrowse" as const, id: "LIST" as const },
    ...(carId ? [{ type: "car" as const, id: carId }] : []),
  ];
}

/**
 * Validates a single car API payload with Zod.
 * @param response unknown - Raw response body from axiosBaseQuery.
 * @returns Car - Parsed car entity.
 */
function parseCarResponse(response: unknown): Car {

  return CarSchema.parse(response);
}

/**
 * Validates a paginated cars browse payload.
 * @param response unknown - Raw response body from axiosBaseQuery.
 * @returns CarsResponse - Parsed paginated cars payload with rows and cursor meta.
 */
function parseCarsBrowseResponse(response: unknown): CarsResponse {

  return CarsPaginatedSchema.parse(response);
}

/**
 * Builds a stable cache key for cars browse queries with object args.
 * @param endpointName string - RTK Query endpoint name.
 * @param queryArgs CarSearchParams - Browse filters, includes, sort, and pagination.
 * @returns string - Serialized cache key for the query args.
 */
function searchCarsBrowseSerializeQueryArgs({ endpointName, queryArgs }: { endpointName: string; queryArgs: CarSearchParams }): string {
  const { useCache: _omit, ...cacheKeyArgs } = queryArgs;

  return `${endpointName}:${stableSerialize(cacheKeyArgs)}`;
}

/**
 * Builds the axios request for cars browse search.
 * @param params CarSearchParams - Browse filters, includes, sort, and pagination.
 * @returns object - URL, method, and POST body for cars/search.
 */
function searchCarsBrowseQuery({ include, per_page, useCache, ...params }: CarSearchParams) {

  return {
    url: "cars/search",
    method: "POST" as const,
    // Apply browse defaults and normalize filter for Spatie search API
    data: getCarSearchBody({
      include: include ?? CAR_BROWSE_INCLUDES,
      per_page: per_page ?? CAR_BROWSE_PER_PAGE,
      useCache,
      ...params,
    }),
  };
}

/**
 * Builds the axios request to fetch a single car by id.
 * @param carId string | number - Car primary key.
 * @returns object - URL and method for GET cars/:id.
 */
function getCarByIdQuery(carId: string | number) {

  return {
    url: `cars/${carId}`,
    method: "GET" as const,
    params: {
      include: "customData",
    },
  };
}

/**
 * Resolves RTK Query cache tags for a single car detail query.
 * @param _res Car | undefined - Successful query result (unused).
 * @param _err unknown - Query error (unused).
 * @param carId string | number - Car primary key from query args.
 * @returns Array - Cache tag for the car detail entity.
 */
function getCarByIdProvidesTags(_res: Car | undefined, _err: unknown, carId: string | number) {

  return [{ type: "car" as const, id: Number(carId) }];
}

/**
 * Builds the axios request to create a car.
 * @param carToAdd CarAdd - Payload for the new car.
 * @returns object - URL, method, and POST body for cars.
 */
function addCarQuery(carToAdd: CarAdd) {

  return {
    url: "cars",
    method: "POST" as const,
    data: carToAdd,
  };
}

/**
 * Resolves RTK Query cache tags to invalidate after creating a car.
 * @returns Array - Cache tags for cars list and browse list.
 */
function addCarInvalidatesTags() {

  return carMutationTags();
}

/**
 * Builds the axios request to update a car.
 * @param arg object - Car id and PATCH payload.
 * @returns object - URL, method, and PATCH body for cars/:id.
 */
function saveCarQuery({ carId, carToSave }: { carId: number; carToSave: CarEdit }) {

  return {
    url: `cars/${carId}`,
    method: "PATCH" as const,
    data: carToSave,
  };
}

/**
 * Resolves RTK Query cache tags to invalidate after updating a car.
 * @param _res Car | undefined - Successful mutation result (unused).
 * @param _err unknown - Mutation error (unused).
 * @param arg object - Mutation arg containing carId.
 * @returns Array - Cache tags for lists and the updated car detail.
 */
function saveCarInvalidatesTags(_res: Car | undefined, _err: unknown, { carId }: { carId: number; carToSave: CarEdit }) {

  return carMutationTags(carId);
}

/**
 * Builds the axios request to delete a car.
 * @param carId number - Car primary key.
 * @returns object - URL and method for DELETE cars/:id.
 */
function deleteCarQuery(carId: number) {

  return {
    url: `cars/${carId}`,
    method: "DELETE" as const,
  };
}

/**
 * Resolves RTK Query cache tags to invalidate after deleting a car.
 * @param _res void | undefined - Successful mutation result (unused).
 * @param _err unknown - Mutation error (unused).
 * @param carId number - Deleted car primary key.
 * @returns Array - Cache tags for lists and the deleted car detail.
 */
function deleteCarInvalidatesTags(_res: void | undefined, _err: unknown, carId: number) {

  return carMutationTags(carId);
}

/**
 * Builds the axios request for the next available car number.
 * @returns object - URL and method for GET cars/next-number.
 */
function getNextCarNumberQuery() {

  return {
    url: "cars/next-number",
    method: "GET" as const,
  };
}

/**
 * Maps the next-number API payload to a numeric car index.
 * @param response object - Raw response with next_car_index.
 * @returns number - Next car number from the server.
 */
function getNextCarNumberTransformResponse(response: { next_car_index: number }): number {

  return response.next_car_index;
}